import { useSupported } from '@vueuse/core';
import { tryOnScopeDispose } from '@vueuse/shared';
import { computed, ref, toValue, watchEffect } from 'vue';

import type { MaybeRefOrGetter } from 'vue';

// ToDo: remove after fix
export function useMediaQuery(query: MaybeRefOrGetter<string>) {
  const isSupported = useSupported(
    () => typeof window !== 'undefined' && 'matchMedia' in window && typeof window.matchMedia === 'function',
  );

  let mediaQuery: MediaQueryList | undefined;
  const matches = ref(false);

  const handler = (event: MediaQueryListEvent) => {
    matches.value = event.matches;
  };

  const cleanup = () => {
    if (!mediaQuery) {
      return;
    }
    if ('removeEventListener' in mediaQuery) {
      mediaQuery.removeEventListener('change', handler);
    } else {
      // @ts-expect-error deprecated API
      mediaQuery.removeListener(handler);
    }
  };

  const stopWatch = watchEffect(() => {
    if (!isSupported.value) {
      return;
    }

    cleanup();

    mediaQuery = window.matchMedia(toValue(query));

    if ('addEventListener' in mediaQuery) {
      mediaQuery.addEventListener('change', handler);
    } else {
      // @ts-expect-error deprecated API
      mediaQuery.addListener(handler);
    }

    matches.value = mediaQuery.matches;
  });

  tryOnScopeDispose(() => {
    stopWatch();
    cleanup();
    mediaQuery = undefined;
  });

  return matches;
}

const MediaQueries = {
  FROM_DESKTOP_MEDIUM: '(min-width: 1279px)',
  IS_DESKTOP_MEDIUM: '(max-width: 1279px)',
  IS_DESKTOP_SMALL: '(min-width: 1024px)',
  IS_DESKTOP: '(min-width: 1024px) and (max-width: 1279px)',
  IS_MOBILE: '(max-width: 767px)',
  IS_MOBILE_MINI: '(max-width: 360px)',
  IS_TABLET: '(max-width: 1023px)',
  IS_TABLET_MEDIUM: '(max-width: 987px)',
  IS_TABLET_ONLY: '(min-width: 768px) and (max-width: 1023px)',
} as const;
const isMobile = computed(() => {
  return useMediaQuery(MediaQueries.IS_MOBILE).value;
});
const isTabletOnly = computed(() => {
  return useMediaQuery(MediaQueries.IS_TABLET_ONLY).value;
});
const isTablet = computed(() => {
  return useMediaQuery(MediaQueries.IS_TABLET).value;
});
const isTabletMedium = computed(() => {
  return useMediaQuery(MediaQueries.IS_TABLET_MEDIUM).value;
});
const isDesktop = computed(() => {
  return useMediaQuery(MediaQueries.IS_DESKTOP).value;
});

const isDesktopSmall = computed(() => {
  return useMediaQuery(MediaQueries.IS_DESKTOP_SMALL).value;
});
const isDesktopMedium = computed(() => {
  return useMediaQuery(MediaQueries.IS_DESKTOP_MEDIUM).value;
});
const fromDesktopMedium = computed(() => {
  return useMediaQuery(MediaQueries.FROM_DESKTOP_MEDIUM).value;
});
const isMobileMini = computed(() => {
  return useMediaQuery(MediaQueries.IS_MOBILE_MINI).value;
});

export const useScreenSize = () => {
  return {
    fromDesktopMedium,
    isDesktopMedium,
    isDesktopSmall,
    isDesktop,
    isMobile,
    isTablet,
    isTabletMedium,
    isTabletOnly,
    isMobileMini,
  };
};
